import { Pivot, PivotItem, DefaultButton, TextField, IconButton, TooltipHost, Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton as FluentDefaultButton } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { dataTableStyles } from "../styles";
import { userAPI } from "../UserAPI";
import Section from "../components/Section";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { useCustomerTheme } from "../Theme";

const MonitoringPanel: React.FC = () => {
    const agenticColumns = [
        {
            name: 'Name',
            width: '300px',
            selector: (row: any) => row.name,
            cell: (row: any) => <div className="textS">{row.name}</div>,
            sortable: true,
        },
        {
            name: 'Type',
            width: '150px',
            selector: (row: any) => row.type,
            cell: (row: any) => {
                let content;
                switch (row.type) {
                    case 'intent':
                        content = <span className="colorPrimary">Internes Werkzeug</span>;
                        break;
                    case 'mcpTool':
                        content = <span className="colorSecondary">MCP-Werkzeug</span>;
                        break;
                    case 'knowledgeBase':
                        content = <span className="colorContrast">Wissensbasis</span>;
                        break;
                    default:
                        content = row.type;
                }
                return <div className="textS">{content}</div>;
            },
            sortable: true,
        },
        {
            name: 'Provider',
            width: '200px',
            selector: (row: any) => row.provider.name,
            cell: (row: any) => <div className="paddingXS">
                <div className="textS fontWeightBold">{row.provider.name}</div>
                <div className="textS"><a href={row.provider.url} target="_blank" rel="noopener noreferrer">{row.provider.url}</a></div></div>,
            sortable: true,
        },
        {
            name: 'Description',
            selector: (row: any) => row.description,
            cell: (row: any) => <div className="paddingXS displayFlex flexDirectionColumn alignItemsStart">
                <div className="textS">{row.description}</div>
                <div className="marginTopS flexGrow1 displayFlex alignItemsStart justifyContentEnd">
                    {row.examples && row.examples.length > 0 ? (
                        <div
                            className="textS colorGrey hoverHand"
                            style={{ textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={() => {
                                setCurrentExamples(row.examples);
                                setShowExamplesDialog(true);
                            }}
                        >
                            ({row.examples.length} Beispielanfrage(n))
                        </div>
                    ) : (
                        <div className="textS colorGrey">(Keine Beispielanfragen)</div>
                    )}
                </div>
            </div>,
            grow: 2,
        },
    ];

    const [agenticData, setAgenticData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [usageMetrics, setUsageMetrics] = useState<any>(null);
    const [filter, setFilter] = useState("");
    const { t } = useTranslation();
    const { theme } = useCustomerTheme();
    const [showExamplesDialog, setShowExamplesDialog] = useState(false);
    const [currentExamples, setCurrentExamples] = useState<string[]>([]);

    console.log('theme', theme);

    const loadAgenticServices = () => {
        setLoading(true);
        userAPI.getAgenticServices()
            .then(setAgenticData)
            .catch(() => setAgenticData([]))
            .finally(() => setLoading(false));
    };

    const loadUsageMetrics = () => {
        setLoading(true);
        userAPI.getUsageMetrics()
            .then(setUsageMetrics)
            .catch(() => setUsageMetrics(null))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadAgenticServices();
        loadUsageMetrics();
    }, []);

    useEffect(() => {
        console.log('usageMetrics', usageMetrics);
    }, [usageMetrics]);

    useEffect(() => {
        console.log('agenticData', agenticData);
    }, [agenticData]);

    const filteredAgenticData = agenticData.filter((tool: any) => {
        if (!filter.trim()) return true;
        const lower = filter.trim().toLowerCase();
        return (tool.name?.toLowerCase().startsWith(lower) || tool.provider?.name?.toLowerCase().startsWith(lower));
    });

    if (!usageMetrics) {
        return <div>Loading...</div>;
    }

    const chartConfigs = [
        {
            key: 'requestsInYear',
            title: 'Aktuelles Jahr',
            data: usageMetrics?.monthsInCurrentYear || [],
            length: 12,
        },
        {
            key: 'requestsInMonth',
            title: 'Aktueller Monat',
            data: usageMetrics?.daysInCurrentMonth || [],
            length: 31,
        },
        {
            key: 'requestsInDay',
            title: 'Aktueller Tag',
            data: usageMetrics?.hoursInCurrentDay || [],
            length: 24,
        },
        {
            key: 'requestsInHour',
            title: 'Aktuelle Stunde',
            data: usageMetrics?.minutesInCurrentHour || [],
            length: 60,
        },
        {
            key: 'requestsInMinute',
            title: 'Aktuelle Minute',
            data: usageMetrics?.currentMinute?.requests?.perSecond
                ? usageMetrics.currentMinute.requests.perSecond.map((count: number) => ({ count }))
                : [],
            length: 60,
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Pivot>
                <PivotItem headerText="Nutzung">
                    <div className="marginTopM">
                        <Section title="Nutzungsstatistiken">
                            <div className="displayFlex alignItemsCenter marginBottomM" style={{ justifyContent: 'flex-end' }}>
                                <TooltipHost content="Aktualisieren">
                                    <IconButton
                                        iconProps={{ iconName: 'Refresh' }}
                                        ariaLabel="Aktualisieren"
                                        onClick={loadUsageMetrics}
                                        disabled={loading}
                                    />
                                </TooltipHost>
                            </div>
                            {usageMetrics && (
                                <div>
                                    <div className="textHeading">Anfragen</div>
                                    <div className="marginTopM displayFlex gapL" style={{ overflowX: 'auto', alignItems: 'end' }}>
                                        {chartConfigs.map(({ key, title, data, length }, idx) => {
                                            let xaxis = {};
                                            let paddedData = [];
                                            // Helper to normalize entries
                                            const normalize = (entry: any) => {
                                                if (!entry) return { count: 0 };
                                                if (typeof entry.count === 'number') return { count: entry.count };
                                                if (typeof entry.requests === 'number') return { count: entry.requests };
                                                // Fallback: sum all numeric properties (for debugging/robustness)
                                                const numericSum = Object.values(entry).reduce((sum: number, val) =>
                                                    typeof val === 'number' ? sum + val : sum, 0);
                                                return { count: numericSum };
                                            };
                                            // Pad and normalize
                                            const padded = Array.from({ length }, (_, i) => data[i] || null);
                                            const normalized = padded.map(entry => entry ? (entry.count ?? entry.requests ?? 0) : 0);
                                            paddedData = padded.map(normalize);
                                            // X axis config
                                            const chartColors = [theme.tertiary, theme.secondary, theme.primary];
                                            console.log(`${key} chartColors`, chartColors);
                                            const barData = paddedData.map((d: any) => d.count);
                                            console.log(`${key} barData`, barData);
                                            if (key === 'requestsInYear') {
                                                xaxis = {
                                                    categories: Array.from({ length: 12 }, (_, i) => `${i + 1}`),
                                                    labels: {
                                                        formatter: (val: string, i: number) => (i === 5 ? '6' : i === 11 ? '12' : ''),
                                                        rotate: 0
                                                    }
                                                };
                                            } else if (key === 'requestsInMonth') {
                                                xaxis = {
                                                    categories: Array.from({ length: 31 }, (_, i) => `${i + 1}`),
                                                    labels: {
                                                        formatter: (val: string, i: number) => (i === 0 ? '1' : i === 14 ? '15' : i === 30 ? '31' : ''),
                                                        rotate: 0
                                                    }
                                                };
                                            } else if (key === 'requestsInDay') {
                                                xaxis = {
                                                    categories: Array.from({ length: 24 }, (_, i) => `${i}`),
                                                    labels: {
                                                        formatter: (val: string, i: number) => (i === 0 ? '0' : i === 12 ? '12' : i === 23 ? '23' : ''),
                                                        rotate: 0
                                                    }
                                                };
                                            } else if (key === 'requestsInHour' || key === 'requestsInMinute') {
                                                // For hour/minute, aggregate into 12 bins of 5
                                                const bins = Array.from({ length: 12 }, (_, i) => {
                                                    const start = i * 5;
                                                    const end = start + 5;
                                                    return normalized.slice(start, end).reduce((sum, val) => sum + val, 0);
                                                });
                                                paddedData = bins.map(count => ({ count }));
                                                xaxis = {
                                                    categories: ["5", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55", "60"],
                                                    labels: {
                                                        formatter: (val: string) => (["15", "30", "45", "60"].includes(val) ? val : ''),
                                                        rotate: 0
                                                    }
                                                };
                                            }
                                            return (
                                                <div key={key} className="displayFlex flexDirectionColumn alignItemsCenter" style={{ flex: '1 1 0', minWidth: 0 }}>
                                                    <div className="textM fontWeightBold marginBottomS">{title}</div>
                                                    <Chart
                                                        options={{
                                                            chart: { type: 'bar', height: 200 },
                                                            colors: chartColors,
                                                            xaxis,
                                                            yaxis: {},
                                                            dataLabels: { enabled: false },
                                                            tooltip: {
                                                                x: { formatter: (val: any) => val },
                                                                y: { formatter: (val: any) => val }
                                                            }
                                                        }}
                                                        series={[{ name: 'Requests', data: paddedData.map((d: any) => d.count) }]}
                                                        type="bar"
                                                        height={200}
                                                        width="100%"
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Active Users Section */}
                                    <div className="textHeading marginTopXL">Active Users</div>
                                    <div className="marginTopM displayFlex gapL" style={{ overflowX: 'auto', alignItems: 'end' }}>
                                        {chartConfigs.map(({ key, title, data, length }, idx) => {
                                            // Compute active users: login - logout (minimum 0)
                                            let loginData = (usageMetrics?.[key]?.login || usageMetrics?.[key + 's']?.login || []);
                                            let logoutData = (usageMetrics?.[key]?.logout || usageMetrics?.[key + 's']?.logout || []);
                                            // Fallback for legacy/flat structure
                                            if (!Array.isArray(loginData) && Array.isArray(usageMetrics?.[key])) {
                                                loginData = usageMetrics[key].map((entry: any) => entry?.login ?? 0);
                                            }
                                            if (!Array.isArray(logoutData) && Array.isArray(usageMetrics?.[key])) {
                                                logoutData = usageMetrics[key].map((entry: any) => entry?.logout ?? 0);
                                            }
                                            // Pad arrays to length
                                            loginData = Array.from({ length }, (_, i) => loginData?.[i] ?? 0);
                                            logoutData = Array.from({ length }, (_, i) => logoutData?.[i] ?? 0);
                                            const activeUsers = loginData.map((login: number, i: number) => Math.max(0, login - logoutData[i]));
                                            // X axis config (reuse from above)
                                            let xaxis = {};
                                            if (key === 'requestsInYear') {
                                                xaxis = {
                                                    categories: Array.from({ length: 12 }, (_, i) => `${i + 1}`),
                                                    labels: {
                                                        formatter: (val: string, i: number) => (i === 5 ? '6' : i === 11 ? '12' : ''),
                                                        rotate: 0
                                                    }
                                                };
                                            } else if (key === 'requestsInMonth') {
                                                xaxis = {
                                                    categories: Array.from({ length: 31 }, (_, i) => `${i + 1}`),
                                                    labels: {
                                                        formatter: (val: string, i: number) => (i === 0 ? '1' : i === 14 ? '15' : i === 30 ? '31' : ''),
                                                        rotate: 0
                                                    }
                                                };
                                            } else if (key === 'requestsInDay') {
                                                xaxis = {
                                                    categories: Array.from({ length: 24 }, (_, i) => `${i}`),
                                                    labels: {
                                                        formatter: (val: string, i: number) => (i === 0 ? '0' : i === 12 ? '12' : i === 23 ? '23' : ''),
                                                        rotate: 0
                                                    }
                                                };
                                            } else if (key === 'requestsInHour' || key === 'requestsInMinute') {
                                                // For hour/minute, aggregate into 12 bins of 5
                                                const bins = Array.from({ length: 12 }, (_, i) => {
                                                    const start = i * 5;
                                                    const end = start + 5;
                                                    return activeUsers.slice(start, end).reduce((sum: number, val: number) => sum + val, 0);
                                                });
                                                // Replace activeUsers with binned data
                                                activeUsers.splice(0, activeUsers.length, ...bins);
                                                xaxis = {
                                                    categories: ["5", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55", "60"],
                                                    labels: {
                                                        formatter: (val: string) => (["15", "30", "45", "60"].includes(val) ? val : ''),
                                                        rotate: 0
                                                    }
                                                };
                                            }
                                            const chartColors = [theme.primary, theme.secondary, theme.tertiary];
                                            return (
                                                <div key={key} className="displayFlex flexDirectionColumn alignItemsCenter" style={{ flex: '1 1 0', minWidth: 0 }}>
                                                    <div className="textM fontWeightBold marginBottomS">{title}</div>
                                                    <Chart
                                                        options={{
                                                            chart: { type: 'bar', height: 200 },
                                                            colors: chartColors,
                                                            xaxis,
                                                            yaxis: {},
                                                            dataLabels: { enabled: false },
                                                            tooltip: {
                                                                x: { formatter: (val: any) => val },
                                                                y: { formatter: (val: any) => val }
                                                            }
                                                        }}
                                                        series={[{ name: 'Active Users', data: activeUsers }]}
                                                        type="bar"
                                                        height={200}
                                                        width="100%"
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </Section>
                    </div>
                </PivotItem>
                <PivotItem headerText="Agentic">
                    <div className="marginTopM">
                        <Section title="Agentic Tools">
                            <div className="displayFlex alignItemsCenter marginBottomM" style={{ justifyContent: 'space-between' }}>
                                <TextField
                                    value={filter}
                                    onChange={(_, v) => setFilter(v || "")}
                                    placeholder={t('monitoringPanel.filterPlaceholder')}
                                    styles={{ root: { maxWidth: 250 } }}
                                    underlined
                                />
                                <TooltipHost content="Aktualisieren">
                                    <IconButton
                                        iconProps={{ iconName: 'Refresh' }}
                                        ariaLabel="Aktualisieren"
                                        onClick={loadAgenticServices}
                                        disabled={loading}
                                    />
                                </TooltipHost>
                            </div>
                            <div>
                                <DataTable
                                    data={filteredAgenticData}
                                    columns={agenticColumns}
                                    customStyles={dataTableStyles}
                                    pagination
                                    paginationPerPage={5}
                                />
                            </div>
                        </Section>
                    </div>
                </PivotItem>
            </Pivot>
            <Dialog
                hidden={!showExamplesDialog}
                onDismiss={() => setShowExamplesDialog(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Beispielanfragen',
                }}
            >
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                    {currentExamples.map((ex, idx) => (
                        <li key={idx}>{ex}</li>
                    ))}
                </ul>
                <div style={{ marginTop: 32 }}>
                    <DialogFooter>
                        <PrimaryButton onClick={() => setShowExamplesDialog(false)} text="Schließen" />
                    </DialogFooter>
                </div>
            </Dialog>
        </div>
    );
};

export default MonitoringPanel; 